import { createSelector } from 'reselect';
import { AppState, store } from '../../../core/Store';
import { PersonalInfoErrors, State } from './reducer';
import * as RisksSelectors from '../../data/dictionaries/risks/selectors';
import moment from 'moment';
import * as DictionariesConsentsSelectors from '../../data/dictionaries/consents/selectors';

type Selector<T = any> = (redState: State, appState: AppState) => T;
function prepareSelector<T>(selector: Selector<T>) {
	return (state: AppState) => {
		return selector(state.ui.form, state);
	};
}

export const getPersonalInfo = prepareSelector((redState, appState) => {
	return redState.personalInfo;
});

export const getPersonalInfoErrors = prepareSelector((redState, appState) => {
	return redState.personalInfoErrors;
});

export const getPersonalInfoRAdressErrors = prepareSelector(
	(redState, appState) => {
		return redState.personalInfoRAdressErrors;
	}
);

export const getStartDate = prepareSelector((redState, appState) => {
	return redState.startDate;
});

export const getEndDate = prepareSelector((redState, appState) => {
	return redState.startDate
		? moment(redState.startDate)
				.clone()
				.add(1, 'year')
				.subtract(1, 'day')
				.format('YYYY-MM-DD')
		: null;
});

export const getConsentsStep1 = prepareSelector((redState, appState) => {
	return redState.consentsStep1;
});

export const getConsentsStep2 = prepareSelector((redState, appState) => {
	return redState.consentsStep2;
});

export const getSelectedRisksIds = prepareSelector((redState, appState) => {
	return redState.selectedRisksIds;
});

export const getVereficationCode = prepareSelector((redState, appState) => {
	return redState.vereficationCode;
});

export const getPaywallUrl = prepareSelector((redState, appState) => {
	return redState.paywallUrl;
});

export const getContributionSum = createSelector(
	[getSelectedRisksIds],
	(selectedRisksIds) => {
		const state = store.getState();
		const allRisks = [
			...RisksSelectors.getRisksKop(state),
			...RisksSelectors.getRisksNnw(state),
			...RisksSelectors.getRisksNnwKlauzule(state),
			...RisksSelectors.getRisksOc(state),
		];
		const mapped = selectedRisksIds.map(
			(el) =>
				allRisks.find((risk) => risk.riskId === el)!.riskContribution
		);
		return mapped.length > 1
			? mapped.reduce((prev, current) => prev + current)
			: mapped[0];
	}
);

export const getIsLoaded = prepareSelector((redState, appState) => {
	return redState.loaded;
});

export const getVereficationCodeDisplayError = prepareSelector(
	(redState, appState) => {
		return redState.vereficationCodeError;
	}
);

export const getFormAccessible = prepareSelector((redState, appState) => {
	return redState.formAccessible;
});

export const getExtendedOcCoverageSelected = prepareSelector(
	(redState, appState) => {
		return redState.extendedOcCoverageSelected;
	}
);

export const getAllRequiredStage1ConsentsChecked = createSelector(
	[getConsentsStep1, DictionariesConsentsSelectors.getConsentsStage1],
	(consentsStep1State, consentsStage1) => {
		return consentsStage1
			.filter((el) => el.consentRequire)
			.every(
				(el) =>
					consentsStep1State.find((s) => s.consentId === el.consentId)
						?.consentState === true
			);
	}
);

export const getAllRequiredStage2ConsentsChecked = createSelector(
	[getConsentsStep2, DictionariesConsentsSelectors.getConsentsStage2],
	(consentsStep2State, consentsStage2) => {
		return consentsStage2
			.filter((el) => el.consentRequire)
			.every(
				(el) =>
					consentsStep2State.find((s) => s.consentId === el.consentId)
						?.consentState === true
			);
	}
);

export const getHasBusiness = prepareSelector((redState, appState) => {
	return redState.hasBusiness;
});

export const getCanSave = createSelector(
	[
		getPersonalInfoErrors,
		getPersonalInfoRAdressErrors,
		getAllRequiredStage1ConsentsChecked,
		getAllRequiredStage2ConsentsChecked,
		getVereficationCode,
		getHasBusiness,
	],
	(
		personalInfoErrors,
		personalInfoRAdressErrors,
		allRequiredStage1ConsentsChecked,
		allRequiredStage2ConsentsChecked,
		vereficationCode,
		hasBusiness
	) => {
		const personalInfoGeneralErrors: Omit<
			PersonalInfoErrors,
			'businessName' | 'nip'
		> = {
			email: personalInfoErrors.email,
			firstName: personalInfoErrors.firstName,
			lastName: personalInfoErrors.lastName,
			pesel: personalInfoErrors.pesel,
			phone: personalInfoErrors.phone,
		};
		const personalInfoBusinessErrors: Pick<
			PersonalInfoErrors,
			'businessName' | 'nip'
		> = {
			businessName: personalInfoErrors.businessName,
			nip: personalInfoErrors.nip,
		};

		return (
			Object.values(personalInfoGeneralErrors).every((el) => !el) &&
			(hasBusiness
				? Object.values(personalInfoBusinessErrors).every((el) => !el)
				: true) &&
			Object.values(personalInfoRAdressErrors).every((el) => !el) &&
			allRequiredStage1ConsentsChecked &&
			allRequiredStage2ConsentsChecked &&
			!!vereficationCode
		);
	}
);

export const getCanOpenStage1Modal = createSelector(
	[getStartDate, getSelectedRisksIds],
	(startDate, selectedRisksIds) => {
		return (
			!!startDate &&
			(selectedRisksIds.some((el) => el <= 6) ||
				selectedRisksIds.some((el) => el >= 8 && el <= 15))
		); // due to how the app's backend is structured, there is no other way to diff between risk types other than ids; at least one selected OC risk (excluding id 7) or NNW risk is required to proceed
	}
);

export const getFormSuccess = prepareSelector((redState, appState) => {
	return redState.formSuccess;
});

export const getShowStage1Errors = prepareSelector((redState, appState) => {
	return redState.showStage1Error;
});

export const getShowStage2Errors = prepareSelector((redState, appState) => {
	return redState.showStage2Error;
});

export const getTermError = createSelector(
	[getStartDate, getShowStage1Errors],
	(startDate, showStage1Errors) => {
		return showStage1Errors && !startDate
			? 'Należy wskazać dzień rozpoczęcia ubezpieczenia'
			: null;
	}
);

export const getSectionsError = createSelector(
	[
		getShowStage1Errors,
		getSelectedRisksIds,
		RisksSelectors.getRisksNnw,
		RisksSelectors.getRisksOc,
	],
	(showStage2Errors, selectedRisksIds, risksNnw, risksOc) => {
		return showStage2Errors
			? !selectedRisksIds.some(
					(id) =>
						risksNnw.map((el) => el.riskId).includes(id) ||
						risksOc.map((el) => el.riskId).includes(id)
			  )
				? 'Należy wybrać przynajmniej jeden wariant OC lub NNW'
				: null
			: null;
	}
);

export const getFormError = createSelector(
	[getShowStage2Errors, getPersonalInfo, getHasBusiness],
	(showStage2Errors, personalInfo, hasBusiness) => {
		return (showStage2Errors &&
			(!personalInfo.email ||
				!personalInfo.firstName ||
				!personalInfo.lastName ||
				!personalInfo.pesel ||
				!personalInfo.phone ||
				!personalInfo.residentalAddress?.street ||
				!personalInfo.residentalAddress?.houseNo ||
				!personalInfo.residentalAddress?.postalCode ||
				!personalInfo.residentalAddress?.city)) ||
			(hasBusiness
				? !personalInfo.businessName || !personalInfo.nip
				: false)
			? 'Formularz nie jest uzupełniony poprawnie'
			: null;
	}
);

export const getConsentsError = createSelector(
	[getShowStage2Errors, getAllRequiredStage2ConsentsChecked],
	(showStage2Errors, allRequiredStage2ConsentsChecked) => {
		return showStage2Errors && !allRequiredStage2ConsentsChecked
			? 'Należy zaznaczyć wszystkie wymagane zgody'
			: null;
	}
);

export const getVereficationCodeError = createSelector(
	[getShowStage2Errors, getVereficationCode],
	(showStage2Errors, vereficationCode) => {
		return showStage2Errors && !vereficationCode
			? 'Należy podać poprawny kod weryfikacyjny'
			: null;
	}
);
